import { useState, useEffect, useMemo } from 'react';
import { Box, Card, CardContent, Typography, Alert, Skeleton } from '@mui/material';
import { Chart, useChart } from 'components/chart';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { EmployeeProject } from 'axiosApi/models';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import intl from 'components/i18n/ReactIntlWrapper';
import { addMonths, startOfMonth, endOfMonth, isAfter, isBefore, parseISO, format } from 'date-fns';

interface EmployeeAllocationTimelineProps {
  idProject: number;
  disabled?: boolean;
  refreshKey?: number; // Optional key to trigger refresh
}

interface TimelineData {
  employeeName: string;
  startDate: number;
  endDate: number;
  actualEndDate: number; // Store the real end date for tooltip
  allocation: number;
  allocationType: number;
  utilization: number;
}

const HOURS_PER_MONTH = 160; // Full-time allocation baseline
const MAX_FUTURE_MONTHS = 12; // Maximum 12 months from now

// Color scale based on utilization percentage - matches UtilizationReport
// Returns array [lighter, darker] for striped pattern
const getUtilizationColors = (utilization: number): [string, string] => {
  if (utilization <= 20) return ['#66E5FF', '#00D9FF']; // Light cyan + Cyan (0-20% - low)
  if (utilization <= 50) return ['#B3F4B3', '#90EE90']; // Light green + Green (21-50% - medium)
  if (utilization <= 80) return ['#FFD090', '#FFB547']; // Light orange + Orange (51-80% - high)
  if (utilization <= 100) return ['#FF7380', '#FF4560']; // Light red + Red (81-100% - full)
  return ['#CC0000', '#8B0000']; // Dark red + Darker red (>100% - over-allocated)
};

// Get single color for legend
const getUtilizationColor = (utilization: number): string => {
  const [, darker] = getUtilizationColors(utilization);
  return darker;
};

export const EmployeeAllocationTimeline = ({ idProject, disabled, refreshKey }: EmployeeAllocationTimelineProps) => {
  const api: Api = useApi();
  const toast = useToastMessageQueue();
  const [loading, setLoading] = useState<boolean>(true);
  const [errors, setErrors] = useState<any>({});
  const [employeeProjects, setEmployeeProjects] = useState<EmployeeProject[]>([]);

  const maxFutureDate = useMemo(() => {
    return endOfMonth(addMonths(new Date(), MAX_FUTURE_MONTHS));
  }, []);

  const fetchEmployeesByProject = async (idProject: number) => {
    setLoading(true);
    try {
      const response = await api.projectApi.apiVversionEmployeeProjectGetByProjectIdIdGet(
        idProject,
        '1',
        {}
      );
      
      if (response.data.data?.employeeProjects) {
        const now = new Date();
        const allEmployeeProjects = response.data.data.employeeProjects as EmployeeProject[];
        
        // First, find employees who have at least one active allocation
        const activeEmployeeIds = new Set(
          allEmployeeProjects
            .filter((ep) => {
              if (ep.isDeleted || !ep.startDate || !ep.employee) {
                return false;
              }
              
              // If there's no due date, it's active
              if (!ep.dueDate) {
                return true;
              }
              
              const dueDate = new Date(ep.dueDate);
              
              // If due date is year 3000 or beyond, it's active
              if (dueDate.getFullYear() >= 3000) {
                return true;
              }
              
              // Otherwise, only active if due date is in the future
              return dueDate >= now;
            })
            .map((ep) => ep.employee!.id)
        );
        
        // Now get ALL allocations (including past ones) for those active employees
        const allocationsForActiveEmployees = allEmployeeProjects.filter((ep) => {
          if (ep.isDeleted || !ep.startDate || !ep.employee) {
            return false;
          }
          
          // Include this allocation if the employee has at least one active allocation
          return activeEmployeeIds.has(ep.employee.id);
        });
        
        setEmployeeProjects(allocationsForActiveEmployees);
      }
    } catch (error) {
      handleAPIError(error, toast, errors);
      setErrors({ ...errors });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (idProject) {
      fetchEmployeesByProject(idProject);
    }
  }, [idProject]);

  // Refresh chart when refreshKey changes (triggered by parent after CRUD operations)
  useEffect(() => {
    if (idProject && refreshKey !== undefined) {
      fetchEmployeesByProject(idProject);
    }
  }, [refreshKey, idProject]);

  // Refresh chart when window regains focus (e.g., after editing in a modal/drawer)
  useEffect(() => {
    if (!idProject) return;
    
    const handleFocus = () => {
      fetchEmployeesByProject(idProject);
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [idProject]);

  const timelineData: TimelineData[] = useMemo(() => {
    // Group allocations by employee to show multiple bars per employee
    const groupedByEmployee = employeeProjects.reduce((acc, ep) => {
      const employeeName = ep.employee
        ? `${ep.employee.name || ''} ${ep.employee.lastName || ''}`.trim()
        : intl.get('employeeAllocation.unknownEmployee');
      
      if (!acc[employeeName]) {
        acc[employeeName] = [];
      }

      const startDate = ep.startDate ? new Date(ep.startDate) : new Date();
      
      // Handle end date - cap at 12 months from now if year is 3000 or beyond
      let actualEndDate: Date; // The real end date for tooltip
      if (ep.dueDate) {
        const dueDate = new Date(ep.dueDate);
        // If year is 3000 or later, or date is more than 12 months in the future, cap it
        if (dueDate.getFullYear() >= 3000 || isAfter(dueDate, maxFutureDate)) {
          actualEndDate = maxFutureDate;
        } else {
          actualEndDate = dueDate;
        }
      } else {
        // No end date - use max future date
        actualEndDate = maxFutureDate;
      }

      // For display: ensure minimum bar width for visibility (3 days minimum)
      let displayEndDate = actualEndDate;
      const minDuration = 3 * 24 * 60 * 60 * 1000; // 3 days in milliseconds for better visibility
      if (actualEndDate.getTime() - startDate.getTime() < minDuration) {
        displayEndDate = new Date(startDate.getTime() + minDuration);
      }

      // Calculate utilization percentage
      let utilization = 0;
      if (ep.allocationType === 2) {
        // Hours-based allocation
        utilization = ((ep.allocation || 0) / HOURS_PER_MONTH) * 100;
      } else if (ep.allocationType === 1) {
        // Percentage-based allocation
        const percentValue = ep.allocation || 0;
        // If value is less than 1, it's stored as decimal (0.75 = 75%), otherwise it's already a percentage
        utilization = percentValue < 1 ? percentValue * 100 : percentValue;
      }

      acc[employeeName].push({
        employeeName,
        startDate: startDate.getTime(),
        endDate: displayEndDate.getTime(), // Display end date (with min duration)
        actualEndDate: actualEndDate.getTime(), // Real end date for tooltip
        allocation: ep.allocation || 0,
        allocationType: ep.allocationType || 1,
        utilization: Math.round(utilization * 10) / 10, // Round to 1 decimal
      });

      return acc;
    }, {} as Record<string, TimelineData[]>);

    // Flatten back to array with all allocations (multiple per employee if they exist)
    return Object.values(groupedByEmployee).flat();
  }, [employeeProjects, maxFutureDate]);

  const chartSeries = useMemo(() => {
    // Single series with all data points to avoid rendering issues
    return [{
      name: 'Allocation',
      data: timelineData.map((data) => {
        const [, darkColor] = getUtilizationColors(data.utilization);
        return {
          x: data.employeeName,
          y: [data.startDate, data.endDate],
          fillColor: darkColor,
          allocation: data.allocation,
          allocationType: data.allocationType,
          utilization: data.utilization,
        };
      }),
    }];
  }, [timelineData]);

  const chartOptions = useChart({
    chart: {
      type: 'rangeBar',
      toolbar: {
        show: true,
        tools: {
          download: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true,
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: 28,
        rangeBarGroupRows: false,
        borderRadius: 2,
        distributed: true,
      },
    },
    colors: timelineData.map((data) => {
      const [lightColor] = getUtilizationColors(data.utilization);
      return lightColor;
    }),
    fill: {
      type: 'solid',
      opacity: 1, 
      pattern: {
        style: 'solid',
        width: 10,
        height:40,
        strokeWidth:3,
      }
    },
    xaxis: {
      type: 'datetime',
      labels: {
        datetimeUTC: false,
        format: 'MMM yyyy',
      },
    },
    yaxis: {
      show: true,
      labels: {
        style: {
          fontSize: '12px',
        },
      },
    },
    tooltip: {
      custom: function ({ seriesIndex, dataPointIndex, w }) {
        const data = timelineData[dataPointIndex];
        const startDateStr = format(new Date(data.startDate), 'MMM dd, yyyy');
        const endDateStr = format(new Date(data.actualEndDate), 'MMM dd, yyyy'); // Use actual end date
        
        const allocationText = data.allocationType === 2
          ? `${data.allocation} ${intl.get('employeeAllocation.hours')}`
          : `${data.allocation}%`;

        return `
          <div style="padding: 12px; background: white; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.15);">
            <div style="font-weight: bold; margin-bottom: 8px; color: #333;">${data.employeeName}</div>
            <div style="font-size: 12px; color: #666; margin-bottom: 4px;">
              <strong>${intl.get('employeeAllocation.startDate')}:</strong> ${startDateStr}
            </div>
            <div style="font-size: 12px; color: #666; margin-bottom: 4px;">
              <strong>${intl.get('employeeAllocation.endDate')}:</strong> ${endDateStr}
            </div>
            <div style="font-size: 12px; color: #666; margin-bottom: 4px;">
              <strong>${intl.get('employeeAllocation.allocation')}:</strong> ${allocationText}
            </div>
            <div style="font-size: 12px; color: #666;">
              <strong>${intl.get('employeeAllocation.utilization')}:</strong> ${data.utilization}%
            </div>
          </div>
        `;
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val, opts) {
        const dataIndex = opts.dataPointIndex;
        const utilization = timelineData[dataIndex]?.utilization || 0;
        return `${utilization.toFixed(1)}%`;
      },
      style: {
        fontSize: '18px',
        fontWeight: 'bold',
        colors: ['#000000'],
      },
      background: {
        enabled: false,
      },
      offsetY: 2,
    },
    legend: {
      show: false,
    },
    grid: {
      padding: {
        top: 0,
        right: 20,
        bottom: 0,
        left: 0,
      },
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    stroke: {
      width: 1,
      colors: ['transparent'],
    },
  });

  if (loading) {
    return (
      <Card variant="outlined" sx={{ p: 2, mt: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {intl.get('employeeAllocation.title')}
          </Typography>
          <Skeleton variant="rectangular" height={300} />
        </CardContent>
      </Card>
    );
  }

  if (employeeProjects.length === 0) {
    return (
      <Card variant="outlined" sx={{ p: 2, mt: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {intl.get('employeeAllocation.title')}
          </Typography>
          <Alert severity="info">{intl.get('employeeAllocation.noData')}</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ p: 2, mt: 4 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {intl.get('employeeAllocation.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {intl.get('employeeAllocation.description')}
        </Typography>
        
        {/* Color Legend */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: getUtilizationColor(10),
                borderRadius: 0.5,
              }}
            />
            <Typography variant="caption">0-20%</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: getUtilizationColor(35),
                borderRadius: 0.5,
              }}
            />
            <Typography variant="caption">21-50%</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: getUtilizationColor(65),
                borderRadius: 0.5,
              }}
            />
            <Typography variant="caption">51-80%</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: getUtilizationColor(90),
                borderRadius: 0.5,
              }}
            />
            <Typography variant="caption">81-100%</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: getUtilizationColor(120),
                borderRadius: 0.5,
              }}
            />
            <Typography variant="caption">&gt;100%</Typography>
          </Box>
        </Box>

        <Box 
          sx={{ 
            minHeight: 200, 
            mt: 2,
            '& .apexcharts-bar-area': {
              strokeWidth: 0,
            },
            '& .apexcharts-rangebar-area': {
              strokeWidth: 2,
            }
          }}
        >
          <Chart
            type="rangeBar"
            series={chartSeries}
            options={chartOptions}
            height={Math.max(200, timelineData.length * 40 + 60)}
          />
        </Box>
      </CardContent>
    </Card>
  );
};
